import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { ResponsiveLine } from '@nivo/line';
import dayjs from '@tt/dates/dayjs';

import { FORMAT } from '../../../utilities/functions';

import './styles.scss';

const PerformanceGapChart = ({ data, sparkLineMode = false }) => {
    const [labelFormat, setLabelFormat] = useState({ tickValues: 'every 1 days', format: '%b %Y' });

    useEffect(() => {
        if (data && data.disciplinedCurve && data.disciplinedCurve.length > 0) {
            const curve = data.disciplinedCurve;
            const rangeStart = dayjs(curve[0].date);
            const rangeEnd = dayjs(curve[curve.length - 1].date);
            const days = Math.abs(rangeStart.diff(rangeEnd, 'day'));

            if (days <= 7) {
                setLabelFormat({ tickValues: 'every 1 days', format: '%b %d' });
            } else if (days > 7 && days <= 15) {
                setLabelFormat({ tickValues: 'every 2 days', format: '%b %d' });
            } else if (days > 15 && days <= 30) {
                setLabelFormat({ tickValues: 'every 7 days', format: '%b %d' });
            } else if (days > 30 && days <= 90) {
                setLabelFormat({ tickValues: 'every 15 days', format: '%b %d' });
            } else if (days > 90 && days <= 180) {
                setLabelFormat({ tickValues: 'every 1 month', format: '%b %Y' });
            } else if (days > 180 && days <= 365) {
                setLabelFormat({ tickValues: 'every 2 month', format: '%b %Y' });
            } else if (days > 365 && days <= 730) {
                setLabelFormat({ tickValues: 'every 4 month', format: '%b %Y' });
            } else if (days > 730) {
                setLabelFormat({ tickValues: 'every 6 month', format: '%b %Y' });
            }
        }
    }, [data]);

    if (!data || (!data.disciplinedCurve?.length && !data.undisciplinedCurve?.length)) {
        return (
            <div className="performance-gap-chart performance-gap-chart--empty">
                <span>No evaluated trades to display</span>
            </div>
        );
    }

    // Transform data for Nivo
    const disciplinedData = (data.disciplinedCurve || []).map((point) => ({
        x: dayjs(point.date).toDate(),
        y: point.cumulativePnl,
        tradeCount: point.tradeCount,
        winRate: point.winRate,
        dayPnl: point.dayPnl
    }));

    const undisciplinedData = (data.undisciplinedCurve || []).map((point) => ({
        x: dayjs(point.date).toDate(),
        y: point.cumulativePnl,
        tradeCount: point.tradeCount,
        winRate: point.winRate,
        dayPnl: point.dayPnl
    }));

    // Add starting point at y = 0 for both curves
    const addStartingPoint = (curveData) => {
        if (curveData.length === 0) return curveData;

        const firstDate = dayjs(curveData[0].x);
        const paddedStart = firstDate.subtract(1, 'day').toDate();
        return [{ x: paddedStart, y: 0, tradeCount: 0, winRate: 0, dayPnl: 0 }, ...curveData];
    };

    const chartData = [];

    if (disciplinedData.length > 0) {
        chartData.push({
            id: 'Disciplined',
            data: addStartingPoint(disciplinedData)
        });
    }

    if (undisciplinedData.length > 0) {
        chartData.push({
            id: 'Undisciplined',
            data: addStartingPoint(undisciplinedData)
        });
    }

    const colors = {
        Disciplined: '#29a373',
        Undisciplined: '#E57373'
    };

    return (
        <div
            className={`performance-gap-chart ${
                sparkLineMode ? 'performance-gap-chart--sparkline' : ''
            }`}
        >
            <ResponsiveLine
                data={chartData}
                sliceTooltip={(point) => {
                    const disciplinedPoint = point.slice.points.find(
                        (p) => p.serieId === 'Disciplined'
                    );
                    const undisciplinedPoint = point.slice.points.find(
                        (p) => p.serieId === 'Undisciplined'
                    );

                    return (
                        <div className="performance-gap-tooltip">
                            <div className="performance-gap-tooltip__date">
                                {dayjs(point.slice.points[0].data.x).format('MMMM D, YYYY')}
                            </div>
                            {disciplinedPoint && (
                                <div className="performance-gap-tooltip__row performance-gap-tooltip__row--disciplined">
                                    <span className="performance-gap-tooltip__label">
                                        Disciplined:
                                    </span>
                                    <span className="performance-gap-tooltip__value">
                                        {FORMAT.NUMBER_TO_CURRENCY(disciplinedPoint.data.y)}
                                    </span>
                                </div>
                            )}
                            {undisciplinedPoint && (
                                <div className="performance-gap-tooltip__row performance-gap-tooltip__row--undisciplined">
                                    <span className="performance-gap-tooltip__label">
                                        Undisciplined:
                                    </span>
                                    <span className="performance-gap-tooltip__value">
                                        {FORMAT.NUMBER_TO_CURRENCY(undisciplinedPoint.data.y)}
                                    </span>
                                </div>
                            )}
                            {disciplinedPoint && undisciplinedPoint && (
                                <div className="performance-gap-tooltip__row performance-gap-tooltip__row--gap">
                                    <span className="performance-gap-tooltip__label">Gap:</span>
                                    <span className="performance-gap-tooltip__value">
                                        {FORMAT.NUMBER_TO_CURRENCY(
                                            disciplinedPoint.data.y - undisciplinedPoint.data.y
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                }}
                curve="linear"
                isInteractive={!sparkLineMode}
                margin={
                    sparkLineMode
                        ? { top: 2, bottom: 2, left: 15, right: 15 }
                        : { top: 20, right: 110, bottom: 60, left: 60 }
                }
                xScale={{
                    type: 'time',
                    format: '%Y-%m-%d',
                    precision: 'day',
                    useUTC: false
                }}
                yScale={{
                    type: 'linear',
                    min: 'auto',
                    max: 'auto',
                    stacked: false
                }}
                yFormat=">-.2f"
                axisTop={null}
                axisRight={null}
                axisBottom={
                    sparkLineMode
                        ? null
                        : {
                              tickValues: labelFormat.tickValues,
                              tickSize: 5,
                              tickPadding: 5,
                              tickRotation: 0,
                              format: labelFormat.format
                          }
                }
                axisLeft={
                    sparkLineMode
                        ? null
                        : {
                              tickSize: 5,
                              tickPadding: 5,
                              tickRotation: 0,
                              format: (v) => `$${v}`
                          }
                }
                enableGridX={false}
                enableGridY={!sparkLineMode}
                gridYValues={5}
                enablePoints={!sparkLineMode && chartData[0]?.data?.length <= 30}
                pointSize={6}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                lineWidth={2}
                enableSlices="x"
                useMesh={true}
                colors={(d) => colors[d.id]}
                legends={
                    sparkLineMode
                        ? []
                        : [
                              {
                                  anchor: 'bottom-right',
                                  direction: 'column',
                                  justify: false,
                                  translateX: 100,
                                  translateY: 0,
                                  itemsSpacing: 4,
                                  itemDirection: 'left-to-right',
                                  itemWidth: 90,
                                  itemHeight: 20,
                                  itemOpacity: 0.85,
                                  symbolSize: 12,
                                  symbolShape: 'circle',
                                  effects: [
                                      {
                                          on: 'hover',
                                          style: {
                                              itemOpacity: 1
                                          }
                                      }
                                  ]
                              }
                          ]
                }
            />
            {!sparkLineMode && data.summary && (
                <div className="performance-gap-chart__summary">
                    <div className="performance-gap-chart__summary-item">
                        <span className="performance-gap-chart__summary-label">
                            Performance Gap:
                        </span>
                        <span
                            className={`performance-gap-chart__summary-value ${
                                data.summary.performanceGap >= 0 ? 'positive' : 'negative'
                            }`}
                        >
                            {FORMAT.NUMBER_TO_CURRENCY(data.summary.performanceGap)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

PerformanceGapChart.propTypes = {
    data: PropTypes.shape({
        disciplinedCurve: PropTypes.array,
        undisciplinedCurve: PropTypes.array,
        summary: PropTypes.shape({
            disciplinedFinalPnl: PropTypes.number,
            undisciplinedFinalPnl: PropTypes.number,
            performanceGap: PropTypes.number
        })
    }),
    sparkLineMode: PropTypes.bool
};

export default PerformanceGapChart;
